import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Loader,
  ModalComponentWrapper,
  FormFieldSelectInput,
  AssetBaseInfo,
  LanguageInfo,
  ApiErrorText,
} from '@/shared/components';
import {
  useAppSettingsDictionary,
  useAssetsIcons,
  useCountriesDictionary,
} from '@/shared/api';
import {
  useDisplaySettingsForm,
  useDisplaySettingsFormData,
  type BaseAssetSelectItem,
  type DateFormatSelectItem,
  type DisplaySettingsFormValues,
  type ThemeSelectItem,
  type TimeZoneSelectItem,
} from '../model';
import { ThemeBaseInfo } from './ThemeBaseInfo';
import { useAppSettingsStore, useDateLocale } from '@/shared/lib';
import type { LanguageSelectOption } from '@/shared/types';
import { tv } from 'tailwind-variants';
import { useMemo } from 'react';
import { format } from 'date-fns';

const classes = tv({
  slots: {
    form: 'w-full',
    content: 'flex flex-col gap-[1.6rem]',
    selectedOptionWrapper: 'gap-[0.8rem] p3 w-full',
    selectedOptionIcon: 'w-[1.8rem] h-[1.8rem]',
    assetSelectedOptionIcon: 'w-[4.4rem] h-[4.4rem]',
    selectedOptionLabel: 'p3 text-primary',
  },
});

interface Props {
  onBack: () => void;
}

const DisplaySettingsModal = ({ onBack }: Props) => {
  const { t } = useTranslation();

  const c = classes();

  const { theme, baseAsset, getLanguage, setTheme, setLanguage, setBaseAsset } =
    useAppSettingsStore();
  const { dateLocale } = useDateLocale();

  const { countriesData, isCountriesLoading, countriesError } =
    useCountriesDictionary();
  const { appSettingsData, isAppSettingsLoading, appSettingsError } =
    useAppSettingsDictionary();
  const { assetsIconsData, isAssetsIconsLoading } = useAssetsIcons();

  const isLoading =
    isCountriesLoading || isAppSettingsLoading || isAssetsIconsLoading;
  const apiError = countriesError || appSettingsError;

  const data = useMemo(
    () => ({
      countries: countriesData?.value ?? [],
      accountAssets: appSettingsData?.value.accountAssets ?? [],
      assetsIcons: assetsIconsData?.value ?? {},
    }),
    [countriesData, appSettingsData, assetsIconsData]
  );

  const { languages, themes, dateFormats, assets, timeZones } =
    useDisplaySettingsFormData(data);
  const { form } = useDisplaySettingsForm({
    language: getLanguage(),
    theme,
    baseAsset,
  });

  if (isLoading) {
    return <Loader />;
  }

  if (apiError) {
    return <ApiErrorText error={apiError} />;
  }

  const formatDate = (date: Date, dateFormat: string) => {
    return format(date, dateFormat, { locale: dateLocale });
  };

  const onSubmit = async (data: DisplaySettingsFormValues) => {
    try {
      await Promise.all([
        setTheme(data.theme),
        setLanguage(data.lang),
        setBaseAsset(data.asset),
      ]);
      onBack();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <ModalComponentWrapper
      title={t('personalAccount.userProfile.displaySettings.title')}
      onBack={onBack}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className={c.form()}>
          <div className={c.content()}>
            <FormFieldSelectInput<LanguageSelectOption>
              name="lang"
              label={t('common.language.placeholder')}
              items={languages}
              renderItem={item => <LanguageInfo language={item} />}
              renderSelectedValue={value => (
                <LanguageInfo
                  language={languages.find(item => item.value === value)}
                  className={c.selectedOptionWrapper()}
                  iconClassName={c.selectedOptionIcon()}
                  labelClassName={c.selectedOptionLabel()}
                />
              )}
            />

            <FormFieldSelectInput<BaseAssetSelectItem>
              name="asset"
              label={t(
                'personalAccount.userProfile.displaySettings.baseAssetPlaceholder'
              )}
              disabled={assets.length <= 1}
              items={assets}
              renderItem={item => (
                <AssetBaseInfo
                  asset={{
                    code: item.value,
                    name: item.name,
                    icon: item.icon,
                  }}
                  className={c.selectedOptionWrapper()}
                  iconClassName={c.assetSelectedOptionIcon()}
                />
              )}
              renderSelectedValue={value => {
                const asset = assets.find(item => item.value === value);

                return (
                  <AssetBaseInfo
                    asset={{
                      code: value,
                      name: asset?.name || '',
                      icon: asset?.icon || undefined,
                    }}
                    className={c.selectedOptionWrapper()}
                    iconClassName={c.selectedOptionIcon()}
                    labelClassName={c.selectedOptionLabel()}
                    showName={false}
                  />
                );
              }}
            />

            <FormFieldSelectInput<ThemeSelectItem>
              name="theme"
              label={t(
                'personalAccount.userProfile.displaySettings.themePlaceholder'
              )}
              items={themes}
              renderItem={item => <ThemeBaseInfo theme={item} />}
              disabled={themes.length <= 1}
              renderSelectedValue={value => (
                <ThemeBaseInfo
                  theme={themes.find(item => item.value === value)}
                  className={c.selectedOptionWrapper()}
                  iconClassName={c.selectedOptionIcon()}
                  labelClassName={c.selectedOptionLabel()}
                />
              )}
            />

            <FormFieldSelectInput<DateFormatSelectItem>
              name="dateFormat"
              disabled
              label={t(
                'personalAccount.userProfile.displaySettings.dateFormatPlaceholder'
              )}
              items={dateFormats}
              renderItem={item => (
                <div className={c.selectedOptionWrapper()}>
                  {formatDate(new Date(), item.value)}
                </div>
              )}
              renderSelectedValue={value => (
                <div className={c.selectedOptionWrapper()}>
                  {formatDate(new Date(), value)}
                </div>
              )}
            />

            <FormFieldSelectInput<TimeZoneSelectItem>
              name="timeZone"
              disabled
              label={t(
                'personalAccount.userProfile.displaySettings.timeZonePlaceholder'
              )}
              items={timeZones}
              renderItem={item => (
                <div className={c.selectedOptionWrapper()}>{item.name}</div>
              )}
              renderSelectedValue={value => {
                const item = timeZones.find(tz => tz.value === value);

                return (
                  <div className={c.selectedOptionWrapper()}>{item?.name}</div>
                );
              }}
            />

            <Button size="lg" type="submit">
              {t(
                'personalAccount.userProfile.displaySettings.submitButtonText'
              )}
            </Button>
          </div>
        </form>
      </Form>
    </ModalComponentWrapper>
  );
};

export { DisplaySettingsModal };
